import React, { useMemo, useState } from 'react';
import { Button, Form, Input, Popconfirm, Select, Table, Tag, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import { showError } from 'utils/exceptionUtils';
import { formatISODateTime } from 'utils/dateTimeUtils';
import {
  GET_EXTERNAL_STATIONS_WITH_PAGINATION,
  ExternalStationItem,
  ExternalStationPageResponse,
} from './graphql/queries/getExternalStations';
import {
  SAVE_EXTERNAL_STATION,
  DELETE_EXTERNAL_STATION,
  RESTORE_EXTERNAL_STATION,
  SaveExternalStationInput,
} from './graphql/mutations/externalStationMutations';
import ExternalStationFormDrawer from './components/ExternalStationFormDrawer';

const STATION_TYPE_LABELS: Record<number, string> = {
  1: 'TEMU_GA',
  2: 'TEMU_SCF',
  3: 'TEMU_HANDOVER',
  4: 'TIKTOK',
};

const STATION_TYPE_OPTIONS = [
  { label: 'TEMU_GA', value: 1 },
  { label: 'TEMU_SCF', value: 2 },
  { label: 'TEMU_HANDOVER', value: 3 },
  { label: 'TIKTOK', value: 4 },
];

const STATUS_OPTIONS = [
  { label: 'Active', value: false },
  { label: 'Deleted', value: true },
];

type FilterValues = {
  name?: string;
  stationType?: number;
  isDeleted?: boolean;
};

const ExternalStationPage: React.FC = () => {
  const [filterForm] = Form.useForm<FilterValues>();
  const [appliedFilters, setAppliedFilters] = useState<FilterValues>({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<ExternalStationItem | null>(null);

  const { data, loading, refetch } = useQuery<ExternalStationPageResponse>(
    GET_EXTERNAL_STATIONS_WITH_PAGINATION,
    {
      variables: {
        filter: {
          name: appliedFilters.name || undefined,
          stationType: appliedFilters.stationType ?? undefined,
          isDeleted: appliedFilters.isDeleted ?? undefined,
        },
        pageRequest: { page: pagination.current, size: pagination.pageSize },
      },
      fetchPolicy: 'network-only',
    }
  );

  const [saveExternalStation] = useMutation(SAVE_EXTERNAL_STATION);
  const [deleteExternalStation] = useMutation(DELETE_EXTERNAL_STATION);
  const [restoreExternalStation] = useMutation(RESTORE_EXTERNAL_STATION);

  const handleFilter = () => {
    const values = filterForm.getFieldsValue();
    setAppliedFilters(values);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    filterForm.resetFields();
    setAppliedFilters({});
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const showNewDrawer = () => {
    setSelectedRecord(null);
    setDrawerMode('create');
    setDrawerVisible(true);
  };

  const showEditDrawer = (record: ExternalStationItem) => {
    setSelectedRecord(record);
    setDrawerMode('edit');
    setDrawerVisible(true);
  };

  const handleDrawerSubmit = async (values: SaveExternalStationInput) => {
    try {
      await saveExternalStation({ variables: { input: values } });
      message.success(drawerMode === 'create' ? 'Created successfully' : 'Updated successfully');
      setDrawerVisible(false);
      refetch();
    } catch (e: any) {
      showError(e);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteExternalStation({ variables: { id } });
      message.success('Deactivated successfully');
      refetch();
    } catch (e: any) {
      showError(e);
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await restoreExternalStation({ variables: { id } });
      message.success('Restored successfully');
      refetch();
    } catch (e: any) {
      showError(e);
    }
  };

  const columns: TableColumnsType<ExternalStationItem> = useMemo(() => [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (v: number) => <span style={{ whiteSpace: 'nowrap' }}>{v}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: 'Sorting Code',
      dataIndex: 'sortingCode',
      key: 'sortingCode',
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: 'Carrier Code',
      dataIndex: 'carrierCode',
      key: 'carrierCode',
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: 'Address Name',
      dataIndex: 'addressName',
      key: 'addressName',
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: 'Station Type',
      dataIndex: 'stationType',
      key: 'stationType',
      render: (v: number | null) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {v != null ? (STATION_TYPE_LABELS[v] ?? v) : '-'}
        </span>
      ),
    },
    {
      title: 'Address Line 1',
      dataIndex: 'addressLine1',
      key: 'addressLine1',
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: 'Timezone',
      dataIndex: 'timezoneLocation',
      key: 'timezoneLocation',
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '-'}</span>,
    },
    {
      title: 'Test',
      dataIndex: 'isTest',
      key: 'isTest',
      render: (isTest: boolean) =>
        isTest
          ? <Tag color="orange">Test</Tag>
          : <Tag>Prod</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'isDeleted',
      key: 'isDeleted',
      render: (isDeleted: boolean) =>
        isDeleted
          ? <Tag color="red">Deleted</Tag>
          : <Tag color="green">Active</Tag>,
    },
    {
      title: 'Updated At',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (v: number | null) => {
        if (v == null) return '-';
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {formatISODateTime(new Date(v * 1000).toISOString())}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: any, record: ExternalStationItem) => (
        <div className="flex gap-2">
          {!record.isDeleted && (
            <>
              <Button
                type="link"
                style={{ paddingLeft: 0 }}
                onClick={() => showEditDrawer(record)}
              >
                Edit
              </Button>
              <Popconfirm
                title="Deactivate this station?"
                onConfirm={() => handleDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" danger style={{ paddingLeft: 0 }}>
                  Deactivate
                </Button>
              </Popconfirm>
            </>
          )}
          {record.isDeleted && (
            <Popconfirm
              title="Restore this station?"
              onConfirm={() => handleRestore(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" style={{ paddingLeft: 0 }}>
                Restore
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ], []);

  const tableData = data?.getExternalStationsWithPagination?.content ?? [];
  const totalElements = data?.getExternalStationsWithPagination?.totalElements ?? 0;

  const drawerInitialValues: SaveExternalStationInput | undefined = selectedRecord
    ? {
        id: selectedRecord.id,
        name: selectedRecord.name,
        sortingCode: selectedRecord.sortingCode ?? '',
        carrierCode: selectedRecord.carrierCode ?? undefined,
        addressName: selectedRecord.addressName ?? '',
        stationType: selectedRecord.stationType ?? undefined,
        addressLine1: selectedRecord.addressLine1,
        addressLine2: selectedRecord.addressLine2 ?? undefined,
        contactName: selectedRecord.contactName ?? undefined,
        phoneNumber: selectedRecord.phoneNumber ?? undefined,
        timezoneLocation: selectedRecord.timezoneLocation,
        country: selectedRecord.country ?? undefined,
        state: selectedRecord.state ?? undefined,
        city: selectedRecord.city ?? undefined,
        zipcode: selectedRecord.zipcode ?? undefined,
        temuWarehouseId: selectedRecord.temuWarehouseId ?? undefined,
        isTest: selectedRecord.isTest,
      }
    : undefined;

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      {/* Filter area */}
      <Form form={filterForm} layout="inline" className="py-3 flex flex-wrap gap-2">
        <Form.Item name="name" style={{ marginBottom: 0 }}>
          <Input placeholder="Name" allowClear style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="stationType" style={{ marginBottom: 0 }}>
          <Select
            placeholder="Station Type"
            options={STATION_TYPE_OPTIONS}
            allowClear
            style={{ width: 160 }}
          />
        </Form.Item>
        <Form.Item name="isDeleted" style={{ marginBottom: 0 }}>
          <Select
            placeholder="Status"
            options={STATUS_OPTIONS}
            allowClear
            style={{ width: 140 }}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" onClick={handleFilter}>Filter</Button>
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button onClick={handleReset}>Reset</Button>
        </Form.Item>
      </Form>

      {/* New button */}
      <div className="flex justify-end mb-3">
        <Button type="primary" onClick={showNewDrawer}>
          <span className="font-bold">New</span>
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
        tableLayout="auto"
        className="mb-4"
      />

      <TablePaginationBar
        total={totalElements}
        current={pagination.current}
        pageSize={pagination.pageSize}
        onChange={(page, size) => setPagination({ current: page, pageSize: size })}
      />

      <ExternalStationFormDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        onSubmit={handleDrawerSubmit}
        initialValues={drawerInitialValues}
        mode={drawerMode}
      />
    </div>
  );
};

export default ExternalStationPage;
